import type { PagedResponse } from '../types/api'
import type {
  CombColumnHistory, CombFinding, CombRun, CombSummary, FindingsQuery,
} from '../types/dataComb'
import { fetchApi } from './api'

/**
 * The Data Comb findings surface.
 *
 * Goes through the shared `fetchApi` rather than a local copy: it reads the
 * response body before deciding the error message, which is the difference
 * between "400 Bad Request" and "'urgent' is not a severity". `auditLogApi.ts`
 * predates that helper being exported and still carries its own copy.
 */

const BASE = '/api/v1/data-comb'

export function getCombSummary(): Promise<CombSummary> {
  return fetchApi<CombSummary>(`${BASE}/summary`)
}

export function getCombRuns(limit = 20): Promise<CombRun[]> {
  return fetchApi<CombRun[]>(`${BASE}/runs?limit=${limit}`)
}

export function getCombFindings(query: FindingsQuery): Promise<PagedResponse<CombFinding>> {
  const params = new URLSearchParams()
  if (query.runId) params.set('runId', query.runId)
  if (query.severity) params.set('severity', query.severity)
  if (query.ruleId) params.set('ruleId', query.ruleId)
  if (query.sourceSystem) params.set('sourceSystem', query.sourceSystem)
  if (query.state) params.set('state', query.state)
  if (query.search) params.set('search', query.search)
  if (query.skip != null) params.set('skip', String(query.skip))
  if (query.top != null) params.set('top', String(query.top))

  const qs = params.toString()
  return fetchApi<PagedResponse<CombFinding>>(`${BASE}/findings${qs ? `?${qs}` : ''}`)
}

/**
 * One finding, from any pass — what `/data-comb?finding=<id>` opens.
 *
 * A 404 (an id past the comb's retention window, or one that never existed —
 * the route's `{id:guid}` constraint 404s a malformed id too) arrives as an
 * `ApiError` with status 404, which the page reads as `errorKind` 'not-found'.
 */
export function getCombFinding(id: string): Promise<CombFinding> {
  return fetchApi<CombFinding>(`${BASE}/findings/${encodeURIComponent(id)}`)
}

/**
 * One column's fill rate across recent passes.
 *
 * Each segment is encoded separately — a schema, table or column name is a SQL
 * identifier, not a path, and `Payables.Invoices` reaching the route as two
 * segments would 404.
 */
export function getColumnHistory(
  schemaName: string,
  tableName: string,
  columnName: string,
  limit = 30,
): Promise<CombColumnHistory> {
  const path = [schemaName, tableName, columnName].map(encodeURIComponent).join('/')
  return fetchApi<CombColumnHistory>(`${BASE}/columns/${path}/history?limit=${limit}`)
}

/**
 * Acknowledges a finding, with an optional note (trimmed; blank sends null).
 *
 * BEARER-ONLY (R3, 2026-10-01). The endpoint is Lighthouse.Admin AND pinned to an Entra sign-in
 * (EtlWriteAuthorization), and refuses the shared API key with a 401 even in Development, so with no Entra token
 * this throws BearerRequiredError without sending anything — it never falls back to the key. Refusals change
 * nothing: 401 (not an Entra sign-in), 403 (no role, or no identifier the audit trail can record), 404 (no such
 * finding — likely past the comb's retention window).
 */
export function acknowledgeFinding(id: string, note?: string): Promise<CombFinding> {
  return fetchApi<CombFinding>(`${BASE}/findings/${encodeURIComponent(id)}/acknowledge`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ note: note?.trim() || null }),
    auth: 'bearer-only',
  })
}

/** Returns a finding to the outstanding list. Bearer-only, gated and refused exactly as {@link acknowledgeFinding}. */
export function unacknowledgeFinding(id: string): Promise<CombFinding> {
  return fetchApi<CombFinding>(`${BASE}/findings/${encodeURIComponent(id)}/acknowledge`, {
    method: 'DELETE',
    auth: 'bearer-only',
  })
}
